/**
 * One service request, as every desk's queue shows it.
 *
 * The same card on the Contracts screen, the lab, the rooms desk and the
 * doctor's panel — only the buttons underneath differ, and those are passed in
 * as children. The left edge is the priority (red for life-threatening), and
 * the chips across the top are the three questions every desk asks: is the
 * money cleared, where is the work, and has the patient paid.
 */

import { Suspense, lazy, useState } from 'react';
import { Badge, Icon } from '../../components/common';
import { usePermissions } from '../../context/PermissionContext';
import { PATIENT_FILE_READERS } from '../../domain/rbac';
import {
  BILLING_META,
  CONFIRMATION_CHANNELS,
  DEPARTMENT_META,
  FINANCE_META,
  PRIORITY_META,
  STATUS_META,
  ageFrom,
  formatDateTime,
  formatMoney,
} from './labels';

// See PatientFileModal for why this is lazy.
const PatientFileModal = lazy(() => import('./PatientFileModal'));

const DECISION_LABEL = { accepted: 'وافق', rejected: 'رفض', modification_requested: 'طلب تعديل' };

const FLAG_CLASS = {
  high: 'text-amber-300',
  low: 'text-sky-300',
  critical: 'font-bold text-red-300',
  normal: 'text-emerald-300',
};

/** What the department produced: lab values, a radiology report, what was dispensed. */
export function RequestResult({ request }) {
  const result = request.result;
  if (!result) return null;

  if (request.department === 'lab') {
    return (
      <div className="space-y-2">
        {result.summary && <p className="text-sm text-slate-200">{result.summary}</p>}
        {Array.isArray(result.values) && result.values.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-500">
                <tr>
                  <th className="p-1 text-right">التحليل</th>
                  <th className="p-1 text-right">النتيجة</th>
                  <th className="p-1 text-right">المعدل الطبيعي</th>
                </tr>
              </thead>
              <tbody>
                {result.values.map((v, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="p-1 text-slate-200">{v.test}</td>
                    <td className={`p-1 ${FLAG_CLASS[v.flag] || 'text-slate-200'}`} dir="ltr">
                      {v.value} {v.unit}
                    </td>
                    <td className="p-1 text-slate-400" dir="ltr">
                      {v.range || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  if (request.department === 'radiology') {
    return (
      <div className="space-y-1 text-sm">
        {result.impression && (
          <p className="text-slate-100">
            <b className="text-purple-300">الانطباع: </b>
            {result.impression}
          </p>
        )}
        {result.report && <p className="whitespace-pre-line text-slate-300">{result.report}</p>}
      </div>
    );
  }

  if (request.department === 'pharmacy' || request.department === 'medication') {
    return (
      <div className="space-y-1 text-sm">
        {(result.dispensed || []).map((d, i) => (
          <p key={i} className="text-slate-200">
            <Icon name="pill" size={12} className="inline text-amber-300" /> {d.name}
            {d.quantity ? ` — ${d.quantity}` : ''}
            {d.instructions ? <span className="text-slate-400"> ({d.instructions})</span> : null}
          </p>
        ))}
        {result.summary && <p className="text-slate-400">{result.summary}</p>}
      </div>
    );
  }

  return result.summary ? <p className="whitespace-pre-line text-sm text-slate-200">{result.summary}</p> : null;
}

export function RequestCard({ request, children, details = null, showPatient = true, showMoney = false, onOpenPatient }) {
  const dept = DEPARTMENT_META[request.department] || { label: request.department, icon: 'clipboard-list', tone: 'cyan' };
  const priority = PRIORITY_META[request.priority] || PRIORITY_META.normal;
  const status = STATUS_META[request.status] || STATUS_META.pending;
  const finance = FINANCE_META[request.financeStatus];
  const billing = BILLING_META[request.billingStatus];
  const age = request.patient ? ageFrom(request.patient.dateOfBirth) : null;
  const { canAny } = usePermissions();
  const [fileOpen, setFileOpen] = useState(false);

  // The unified file from every queue (QA report §1.2): a desk that wired its
  // own patient action keeps it; any other desk that may read files opens the
  // shared file window, so no queue is left without a way into the file.
  const selfOpens = !onOpenPatient && Boolean(request.hospitalPatientId) && canAny(PATIENT_FILE_READERS);
  const openPatient = onOpenPatient
    ? () => onOpenPatient(request.patient)
    : selfOpens
      ? () => setFileOpen(true)
      : null;

  return (
    <article
      className={`request-card request-card--${request.priority || 'normal'} glass-panel rounded-2xl border border-white/5 p-4`}
      data-request-id={request.id}
    >
      <header className="flex flex-wrap items-start gap-3">
        <span className={`tile3d__face tone-${dept.tone} !h-11 !w-11 shrink-0`} aria-hidden="true">
          <Icon name={dept.icon} size={20} className="tile3d__icon" />
        </span>
        {/* A 12rem basis, not flex-1's zero: beside the badges a two-column
            queue left the title ~60px, one letter per line. Now the badges
            wrap under the title instead; on a narrow phone it still shrinks. */}
        <div className="min-w-0 grow basis-48">
          <h3 className="break-words text-base font-bold text-white">{request.title}</h3>
          <p className="text-xs text-slate-400">
            {dept.label} · #{request.id} · {formatDateTime(request.createdAt)}
            {request.requestedByName ? ` · من ${request.requestedByName}` : ''}
          </p>
          {(request.clinicName || (request.department === 'checkup' && request.doctorName)) && (
            <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs font-bold text-cyan-200">
              {request.clinicName && (
                <span className="inline-flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full" style={{ background: request.clinicColor || 'rgb(var(--accent-rgb))' }} aria-hidden="true" />
                  {request.clinicName}
                </span>
              )}
              {request.doctorName && <span>· {request.doctorName}</span>}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Badge className={priority.className} icon={priority.icon}>
            {priority.short}
          </Badge>
          <Badge className={status.className}>{status.label}</Badge>
          {request.receipt ? (
            // Cleared at the front desk: the receipt IS the financial approval
            // (QA report §3.3) — say so, rather than "approved" by nobody.
            <Badge
              className={
                request.billingStatus === 'deferred'
                  ? 'border-amber-500/40 bg-amber-500/15 text-amber-100'
                  : 'border-emerald-500/40 bg-emerald-500/15 text-emerald-100'
              }
              icon="receipt"
            >
              {request.billingStatus === 'deferred' ? 'آجل بإيصال' : 'مسدد بإيصال'}{' '}
              <span dir="ltr" className="font-mono">
                {request.receipt.number}
              </span>
            </Badge>
          ) : (
            finance && <Badge className={finance.className}>{finance.label}</Badge>
          )}
          {showMoney && billing && request.billingStatus !== 'none' && <Badge className={billing.className}>{billing.label}</Badge>}
        </div>
      </header>

      {showPatient && request.patient && (
        <button
          type="button"
          onClick={openPatient || undefined}
          disabled={!openPatient}
          className="patient-row mt-3 flex w-full items-center gap-2 rounded-xl bg-white/[0.03] px-3 py-2 text-right enabled:hover:bg-white/[0.06]"
        >
          <Icon name={request.patient.patientType === 'insured' ? 'shield-plus' : 'user'} size={16} className="text-cyan-300" />
          <span className="min-w-0 flex-1 truncate text-sm font-bold text-white">{request.patient.fullName}</span>
          {age !== null && <span className="text-xs text-slate-400">{age} سنة</span>}
          {request.patient.patientType === 'insured' && request.patient.insuranceProvider && (
            <span className="truncate text-xs text-purple-300">{request.patient.insuranceProvider}</span>
          )}
          {openPatient && (
            <span className="patient-row__cta inline-flex shrink-0 items-center gap-1 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2 py-0.5 text-[11px] font-bold text-cyan-200">
              <Icon name="file-heart" size={12} /> الملف الطبي
            </span>
          )}
        </button>
      )}

      {fileOpen && (
        <Suspense fallback={null}>
          <PatientFileModal
            patient={{ id: request.hospitalPatientId, fullName: request.patient?.fullName }}
            onClose={() => setFileOpen(false)}
          />
        </Suspense>
      )}

      {Array.isArray(request.items) && request.items.length > 0 && (
        <ul className="mt-3 space-y-1 text-sm">
          {request.items.map((item, i) => (
            <li key={i} className="flex flex-wrap justify-between gap-2 text-slate-200">
              <span>
                • {item.name}
                {item.quantity > 1 ? ` × ${item.quantity}` : ''}
                {item.instructions ? <span className="text-slate-400"> — {item.instructions}</span> : null}
              </span>
              {showMoney && item.unitPrice !== null && item.unitPrice !== undefined && (
                <span className="text-xs text-slate-400">{formatMoney(item.unitPrice * (item.quantity || 1))}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {request.notes && <p className="mt-2 whitespace-pre-line text-xs text-slate-400">ملاحظات: {request.notes}</p>}

      {request.department === 'surgery' && (
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <Badge className="border-white/10 bg-white/5 text-slate-300" icon="door-open">
            غرفة العمليات: {DECISION_LABEL[request.roomDecision] || 'لم تقرر'}
          </Badge>
          <Badge className="border-white/10 bg-white/5 text-slate-300" icon="stethoscope">
            الجراح{request.doctorName ? ` (${request.doctorName})` : ''}: {DECISION_LABEL[request.doctorDecision] || 'لم يقرر'}
          </Badge>
          {request.scheduledAt && (
            <Badge className="border-rose-500/30 bg-rose-500/10 text-rose-200" icon="calendar-clock">
              {formatDateTime(request.scheduledAt)}
            </Badge>
          )}
          {/* A desk recorded the surgeon's answer after confirming with them
              (SURGERY_CONFIRM_ON_BEHALF) — say whose hand and how. */}
          {request.doctorDecision && request.doctorDecisionOnBehalf && (
            <Badge className="on-behalf-badge" icon={(CONFIRMATION_CHANNELS[request.doctorConfirmedVia] || CONFIRMATION_CHANNELS.phone).icon}>
              سجّله {request.doctorDecisionByName || 'الاستقبال'} بالنيابة عن الجراح ·{' '}
              {(CONFIRMATION_CHANNELS[request.doctorConfirmedVia] || CONFIRMATION_CHANNELS.phone).label}
            </Badge>
          )}
        </div>
      )}

      {request.financeNote && !request.receipt && (
        <p className="mt-2 rounded-lg bg-purple-500/10 px-3 py-1.5 text-xs text-purple-200">التعاقدات: {request.financeNote}</p>
      )}
      {request.decisionNote && (
        <p className="mt-2 rounded-lg bg-amber-500/10 px-3 py-1.5 text-xs text-amber-200">
          {request.status === 'rejected' ? 'سبب الرفض' : 'ملاحظة القسم'}: {request.decisionNote}
        </p>
      )}

      {showMoney && request.quote && (
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <Badge className="border-white/10 bg-white/5 text-slate-200">الإجمالي {formatMoney(request.quote.total)}</Badge>
          <Badge className="border-purple-500/30 bg-purple-500/10 text-purple-200">التأمين {formatMoney(request.quote.covered)}</Badge>
          <Badge className="border-amber-500/40 bg-amber-500/15 text-amber-100">
            المستحق {formatMoney(request.amountDue ?? request.quote.patientShare)}
          </Badge>
        </div>
      )}

      {request.status === 'completed' && request.result && (
        <div className="mt-3 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.06] p-3">
          <p className="mb-1 flex items-center gap-1.5 text-xs font-bold text-emerald-300">
            <Icon name="file-check-2" size={13} /> النتيجة {request.completedAt ? `· ${formatDateTime(request.completedAt)}` : ''}
            {request.handledByName ? ` · ${request.handledByName}` : ''}
          </p>
          <RequestResult request={request} />
        </div>
      )}

      {details}

      {children && <footer className="mt-4 flex flex-wrap gap-2 border-t border-white/5 pt-3">{children}</footer>}
    </article>
  );
}

export default RequestCard;
